import {
  ABTestAPIResponse,
  ABTestDb,
  ABTestGroup,
  ABTestScenario,
  Nullable,
  Undefinedable,
} from "@reco/shared/src/types/abTest";
export default class ABTest {
  public static AB_TEST_DB_KEY = "recommendationsAB";

  public static deleteABTestDb() {
    if (typeof localStorage === "undefined") {
      return;
    }
    localStorage.removeItem(ABTest.AB_TEST_DB_KEY);
  }

  public static deleteABTestScenario(scenario: string) {
    if (!scenario) {
      return;
    }
    const abTestDb: Undefinedable<ABTestDb> = ABTest.getABTestDb();
    if (!abTestDb) {
      return;
    }
    delete abTestDb[scenario];
    localStorage.setItem(this.AB_TEST_DB_KEY, JSON.stringify(abTestDb));
  }

  public static saveABTestScenario(scenario: string, startDate: string, endDate: string, group: string) {
    const abTestDb: ABTestDb = ABTest.getABTestDb() || {};
    abTestDb[scenario] = {
      startDate,
      endDate,
      group,
    };
    localStorage.setItem(this.AB_TEST_DB_KEY, JSON.stringify(abTestDb));
  }

  public static handleABResponse(scenario: string, response?: ABTestAPIResponse): Undefinedable<ABTestScenario> {
    if (!scenario) {
      return;
    }
    // do this check early to delete any expired saved ABTests
    const previousAbTestAssigment: Undefinedable<ABTestScenario> = ABTest.getABTestScenario(scenario);

    if (!response || (response && !response.groups)) {
      return;
    }
    const random = ABTest.getCryptoRandomInt() || ABTest.getRandomInt();
    const groupsIndex = random % response.groups.length;
    const newAssignedGroup: ABTestGroup = response.groups[groupsIndex];
    const previousAssignedGroupFromResponse: Undefinedable<ABTestGroup> =
      previousAbTestAssigment &&
      response.groups.find(
        (responseGroup: ABTestGroup) =>
          responseGroup.group.toUpperCase() === previousAbTestAssigment.group.toUpperCase(),
      );

    const assignedGroup = previousAssignedGroupFromResponse || newAssignedGroup;
    const abTest: ABTestScenario = {
      startDate: response.startDate,
      endDate: response.endDate,
      group: assignedGroup.group,
      reportingValue: assignedGroup.reportingValue,
      reportingDimension: response.reportingDimension,
      recommendationItems: assignedGroup.recommendationItems,
    };

    ABTest.saveABTestScenario(scenario, abTest.startDate, abTest.endDate, abTest.group);
    return abTest;
  }

  public static getCryptoRandomInt(): Nullable<number> {
    const cryptoObj = window.crypto || window.msCrypto;
    if (cryptoObj === undefined || cryptoObj === null) {
      return null;
    }

    if (typeof cryptoObj.getRandomValues === "function" && typeof Uint32Array === "function") {
      return cryptoObj.getRandomValues(new Uint32Array(1))[0];
    } else {
      return null;
    }
  }

  public static getRandomInt(): number {
    const MAX = 1000000;
    return Math.floor(Math.random() * Math.floor(MAX));
  }

  private static getABTestDb(): Undefinedable<ABTestDb> {
    try {
      const abTestDb = JSON.parse(localStorage.getItem(ABTest.AB_TEST_DB_KEY) || "null");
      return abTestDb ? abTestDb : undefined;
    } catch {
      ABTest.deleteABTestDb();
      return undefined;
    }
  }

  private static getABTestScenario(scenario: string): Undefinedable<ABTestScenario> {
    const abTestDb = ABTest.getABTestDb();
    if (!abTestDb || !scenario || (scenario && abTestDb && !abTestDb[scenario])) {
      return undefined;
    }

    const testStart = Date.parse(abTestDb[scenario].startDate || "");
    const testEnd = Date.parse(abTestDb[scenario].endDate || "");
    const isTestExpired = isNaN(testStart) || isNaN(testEnd) || testStart > Date.now() || testEnd < Date.now();

    if (isTestExpired) {
      ABTest.deleteABTestScenario(scenario);
      return undefined;
    }

    return abTestDb[scenario];
  }
}
